import { ScreenManager } from "../../managers/ScreenManager";
import { CardData, OptionsItemLiveData, TextureData } from "../../types/types";
import { ScreenOrientationType } from "../../utils/GetOrientation";
import { GetPointOnCircle } from "../../utils/GetPointOnCircle";
import { Padding } from "../../utils/Padding";
import { Card, CardColor, CardSuit } from "../card/Card";
import { LoaderFileKeys } from "../constant/AppConstants";
import { OptionsDataManager } from "../managers/OptionsDataManager";
import { AppSettings } from "../managers/AppSettings";
import { CardRankProps, CardsManager } from "../managers/CardsManager";
import { LayoutDataManager, OptionsItemKeys } from "../managers/LayoutDataManager";
import { TableScreen } from "../screen/TableScreen";
import { TrumpSuit } from "../state/TableStateMachine";
import { ButtonBubble } from "../ui/ButtonBubble";
import { TextBubble } from "../ui/TextBubble";
import { TextIconBubble } from "../ui/TextIconBubble";
import { CardSuitToTrumpSuit } from "../utils/CardSuitToTrumpSuit";
import { GetCardSuitIconTextureStatesData, TrumpIconType } from "../utils/GetCardSuitIconTextureData";
import { GetOppositePlayerSide } from "../utils/GetOppositePlayerSide";
import { GetOppositeColorSuitToSuit } from "../utils/GetOtherRedSuit";
import { PlayerProps, Players, ReorderCardsProps } from "./Players";
import { AppTimings } from "../data/AppConstants";


export class Player
    extends Phaser.GameObjects.Container
// implements IComponentGameObject
{
    public table: TableScreen;
    public cardsContainer: Phaser.GameObjects.Container;
    public cards: Card[];
    /**
     * Holds 
     * - avatarContainer
     * 
     * Used for scaling animation 'ITS YOUR TURM' - zooms avatar up and down, until card is clicked
     */
    public contentHolder: Phaser.GameObjects.Container;
    /**
     * Avatar container contains
     * - avatar
     * - avatar bg
     * - avatar shadow
     * - avatar label
     * - speech bubble
     * - chip container
     * Avatar container scales based on screen size
     */
    public avatarContainer: Phaser.GameObjects.Container;
    /**
     * Avatar scales based on avatar.scale defined in playerProps.
     */
    public avatar: Phaser.GameObjects.Image;
    public chipContainer: Phaser.GameObjects.Container;
    public avatarBg: Phaser.GameObjects.Image;
    public nsLabelBg: Phaser.GameObjects.NineSlice;
    public labelText: Phaser.GameObjects.Text;
    public acceptorBubble: ButtonBubble;
    private playerName: string;
    private avatarTextureData: TextureData;
    private playerProps: PlayerProps;
    private cardsProps: ReorderCardsProps;
    /**
     * PlayerIndex to player textBubble index
     */
    public index: number;
    public speechBubble: TextIconBubble;
    //@ts-ignore
    private speechBubbleTimer: Phaser.Time.TimerEvent;
    private isShowing: boolean;
    private isPlayer: boolean;
    /**
     * Cached currently popped cards
     * used during orient switch - to pop same cards that were popped in prev. orientation.
     * REASONABLE ALT: or just dont pop them when switching orient (this is something not often done)
     */
    private curPoppedCards: Card[];

    private waitTween: Phaser.Tweens.Tween;
    

    public Init(
        table: TableScreen,
        avatarTextureData: TextureData,
        playerProps: PlayerProps,
        cardsProps: ReorderCardsProps,
        isPlayer: boolean = false,
    ): void {
        // console.log("Player::Init");

        this.table = table;
        this.avatarTextureData = avatarTextureData;
        this.playerProps = playerProps;
        this.cardsProps = cardsProps;
        this.isPlayer = isPlayer;

        this.cards = [];
        // this.curPoppedCards = [];
    }

    public Build(): void {
        this.cardsContainer = this.scene.make.container({});
        this.cardsContainer.setAngle(this.GetFacingAngle());
        this.add(this.cardsContainer);
        this.cardsContainer.setDepth(10);
        
        this.contentHolder = this.scene.make.container({}, false);
        this.add(this.contentHolder);

        this.avatarContainer = this.scene.make.container({}, false);
        this.contentHolder.add(this.avatarContainer);

        // avatarBg: includes shadow
        this.avatarBg = this.scene.make.image({
            key: LoaderFileKeys.avatars,
            frame: "avatar-bg.png"
        });
        this.avatarContainer.add(this.avatarBg);

        this.chipContainer = this.scene.make.container({});
        this.avatarContainer.add(this.chipContainer);

        this.avatar = this.scene.make.image({
            key: this.avatarTextureData.texture,
            frame: this.avatarTextureData.frame
        });
        this.avatar.setOrigin(this.playerProps.dX, this.playerProps.dY);
        this.avatarContainer.add(this.avatar);

        // NOTE: before speech bubble
        // bots have extra 'name' label
        if (!this.isPlayer) {
            const borderSize = 8;
            this.nsLabelBg = this.scene.make.nineslice({
                key: LoaderFileKeys.ui,
                frame: "rect-black-w24-r8.png",
                // width: 256, height: 256,
                height: 60,
                leftWidth: borderSize,
                rightWidth: borderSize,
                topHeight: borderSize,
                bottomHeight: borderSize,
                origin: 0.5,
            });
            this.nsLabelBg.alpha = 0.9;
            this.avatarContainer.add(this.nsLabelBg);

            this.labelText = this.scene.make.text({
                style: {
                    color: "#fff",
                    fontSize: "20px",
                    align: "center",
                    fontFamily: AppSettings.fontFamilies.MontserratBold,
                    metrics: AppSettings.metrics.montserratBold20,
                }
            });
            this.labelText.setOrigin(0.5);
            this.avatarContainer.add(this.labelText);
        }

        // speech bubble
        const bubbleOriginX: number = this.playerProps.dX == -1 ? 0 : this.playerProps.dX;
        const bubbleOriginY: number = this.playerProps.dY == -1 ? 0 : this.playerProps.dY;
        this.speechBubble = new TextIconBubble(this.scene);
        this.speechBubble.Init(
            {texture: LoaderFileKeys.ui, frame: "rect-white-w36-r12-ds.png"},
            21,
            {texture: LoaderFileKeys.ui, frame: "speechBubble-triangle.png"},
            new Padding(9), 
            32, 
            "MontserratBold", 
            0.5
        );
        this.speechBubble.Build();
        this.speechBubble.Hide(false);
        this.avatarContainer.add(this.speechBubble);

        // Player has extra 'acceptor bubble'
        if (this.isPlayer) {
            this.acceptorBubble = new ButtonBubble(this.scene);
            this.acceptorBubble.Init(
                {texture: LoaderFileKeys.ui, frame: "rect-black-w72-r24-A80-ds.png"},
                50,
                {texture: LoaderFileKeys.ui, frame: "speechBubble-triangle.png"},
                new Padding(9),
                30,
                {
                    out: {texture:LoaderFileKeys.ui, frame:"btn-choice-bg-out.png"},
                }
            );
            this.acceptorBubble.Build();
            this.acceptorBubble.SetOrigin(bubbleOriginX, bubbleOriginY);
            this.acceptorBubble.Hide(false);
            this.avatarContainer.add(this.acceptorBubble);
        }

        // update speech bubble view
        this.OnResize(true);// force?
    }

    public Show(animated: boolean = true, onComplete: Function = undefined, triggerResize: boolean = false): void {
        this.alpha = 0;

        // if animate
        if (animated) {
            const genericShowDuration = OptionsDataManager.GetTiming(AppTimings.genericShowDuration);
            this.table.scene.tweens.add({
                // targets: targets,
                targets: this,
                alpha: 1,
                // scale: toAvatarScale,
                duration: genericShowDuration,
                ease: AppSettings.ease.genericShowHideEase,

                // edited
                onComplete: () => {
                    // in case window resized during animation
                    // this.cardsContainer.scale = toCardsContainerScale;
                    onComplete?.();
                },
            });
        }
        else {
            this.alpha = 1;
            onComplete?.();
        }

        // flag showing
        this.isShowing = true;
        if (triggerResize) {
            // now resize once before show (depends on isShowing flag!)
            this.OnResize();
        }
    }

    public Hide(animated: boolean = true, onComplete: Function = undefined): void {
        // kill tweens
        // this.scene.tweens.killTweensOf();

        // children
        this.speechBubble.Hide(animated);

        // if animate
        if (animated) {
            const genericHideDuration = OptionsDataManager.GetTiming(AppTimings.genericHideDuration);
            this.scene.tweens.add({
                // targets: targets,
                targets: this,
                alpha: 0,
                // scale: 0,
                duration: genericHideDuration,
                ease: AppSettings.ease.genericShowHideEase,
                onComplete: () => onComplete?.(),
            })
        }
        else {
            this.alpha = 0;
            onComplete?.();
        }

        // flag showing
        this.isShowing = false;
    }

    public IsShowing(): boolean {
        return this.isShowing;
    }

    public IsPlayer(): boolean {
        return this.isPlayer;
    }

    /**
     * NOTE: Do set variable, but update view only if BOT (only bots have label)
     * @param name 
     */
    public SetName(name: string): void {
        this.playerName = name;
        if (!this.isPlayer) {
            this.labelText.text = name;

            const labelBgWidth = this.labelText.displayWidth + 20;
            const labelBgHeight = this.labelText.displayHeight + 15;
            this.nsLabelBg.setSize(labelBgWidth, labelBgHeight);
            this.nsLabelBg.setPosition(
                this.avatar.x + (0.5 - this.playerProps.dX) * this.avatar.displayWidth,
                this.avatar.y + (1 - this.playerProps.dY) * this.avatar.displayHeight,
            );
            this.labelText.setPosition(this.nsLabelBg.x, this.nsLabelBg.y);
        }
    }

    public SetAvatarFrame(frame: string): void {
        this.avatar.setFrame(frame);
    }

    public GetName(): string {
        return this.playerName;
    }

    public GetPlayerProps(): PlayerProps {
        return this.playerProps;
    }

    public GetCardsProps(): ReorderCardsProps {
        return this.cardsProps;
    }

    /**
     * Used in orient switch @see this.curPoppedCards
     * @returns Currently popped cards
     */
    public GetCurPoppedCards(): Card[] {
        return this.curPoppedCards;
    }

    public Reset() {
        // reset contentHolder scale to 1
        this.ClearWakeupAnimation(false);
        // pool player's cards back to CardsManager
        const pickedCards: Card[] = this.PickAll();
        // pool
        CardsManager.DropCards(pickedCards, false);
        this.speechBubble.Hide(false);
        // reset cur popped cards, making Hint snap to unpopped cards position again on Resize.
        this.curPoppedCards = [];
    }

    public ScheduleWakeupAnimation(): void {
        if (this.index !== Players.PLAYER_INDEX) {
            return;
        }
        let duration: number //= OptionsDataManager.GetTiming(5000);
        duration = 5000;
        this.ClearWakeupAnimation(false);
        this.waitTween = this.scene.tweens.addCounter({
            duration: duration,
            onComplete: ()=>{  
                this.StartWakeupAnimation();
            },
        });
    }

    public StartWakeupAnimation(): void {
        if (this.index !== Players.PLAYER_INDEX) {
            return;
        }
        this.ClearWakeupAnimation(false);
        let duration: number //= OptionsDataManager.GetTiming(500);
        duration = 500;
        this.scene.tweens.add({
            targets: this.contentHolder,
            scale: 1.2,
            yoyo: true,
            repeat: -1,
            duration: duration,
            ease: Phaser.Math.Easing.Quadratic.InOut,
        });
    }

    public ClearWakeupAnimation(animate: boolean = true): void {
        if (this.index !== Players.PLAYER_INDEX) {
            return;
        }
        let duration: number //= OptionsDataManager.GetTiming(250);
        duration = 250;
        // clear scheduling timer (if applicable)
        this.waitTween?.destroy();
        // stop ongoing wakeup animation (if applicable)
        this.scene.tweens.killTweensOf(this.contentHolder);
        if(animate) {
            this.scene.tweens.add({
                targets: this.contentHolder,
                scale: 1,
                duration: duration,
            });
        }
        else {
            this.contentHolder.scale = 1;
        }
    }

    /**
     * 
     * @param text 
     * @param showDuration (optional) Popup should be visible for this duration.
     *  NOTE: Coinche and Contree use 'infinite' show duration for bubbles during bidding phase.
     */
    public Say(
        text: string,
        iconTextureData: TextureData = null,
        animate: boolean = true,
        showDuration?: number,
    ): void {
        // console.log("Say",text);

        this.speechBubble.SetText(text);
        this.speechBubble.SetIconTextureData(iconTextureData);
        this.speechBubble.UpdateView();
        this.speechBubble.Show(animate);

        let simpleDeclarationDuration = showDuration ?? OptionsDataManager.GetTiming(AppTimings.textBubbleDuration);
        // simpleDeclarationDuration = 4000;
        this.speechBubbleTimer = this.scene.time.delayedCall(
            simpleDeclarationDuration,
            () => {
                this.speechBubble.Hide();
            },
            [], this // args? scope?
        );
    }

    /**
     * Drop cards into player's hand
     * - fake this to drop single card by passing [myCard]
     * - rearranges cards upon drop
     * - cards from outside are animated from outside into player's hand
     * @param cards 
     * @param onComplete triggers when onComplete function of card reorder completes.
     */
    public DropCards(
        cards: Card[],
        animateReorder: boolean = true,
        trumpSuit: TrumpSuit = TrumpSuit.UNDEFINED,
        rankLeftToRight: boolean = true,
        stackLeftToRight: boolean = true,
        skipReorder: boolean = false,
        // onReorderComplete: Function = undefined
    ): void {
        // console.log("dropping cards to player index: "+this.index+"; cards:",cards);

        // drop the new cards into this container
        for (let i = 0; i < cards.length; i++) {
            const card: Card = cards[i];
            // quick fix pos
            card.setPosition(0);
            // prep for 'ReorderCards sets scale back to 1'
            card.setScale(0);
            // fix 'duplicate move to container bug'
            // if (card.parentContainer != undefined) {
            //     card.parentContainer.remove(card);
            // }
            this.cardsContainer.add(card);
        }

        // merge card models
        // returns new without modifying original
        this.cards = this.cards.concat(cards);

        // update cards to be frontfacing (or backfacing)
        this.UpdateFacingAll(this.playerProps.cardsContainer.frontFacing);

        // reorder cards data model
        this.ReorderCards(
            animateReorder, // animateReorder?: boolean
            trumpSuit, // trumpSuits?: TrumpSuit
            rankLeftToRight, // rankLeftToRight?: boolean
            stackLeftToRight, // viewLeftToRight?: boolean
            skipReorder, // skipReorder?
            // onReorderComplete, // onComplete?
        );
    }

    /**
     * Drop card into specific index
     * @param cards 
     * @param animate 
     * @param trumpSuit 
     * @param rankLeftToRight 
     * @param stackLeftToRight 
     */
    public DropCardToIndex(card: Card,
        cardIndex: number,
        animate: boolean = true,
        trumpSuit: TrumpSuit = TrumpSuit.UNDEFINED,
        rankLeftToRight: boolean = true,
        stackLeftToRight: boolean = true,
        skipReorder: boolean = false): void {
        // console.log("dropping cards to player index",this.index,"cards",cards)

        // quick fix pos
        // card.setPosition(0); 
        // prep for 'ReorderCards sets scale back to 1'
        // card.setScale(0);
        // fix 'duplicate move to container bug'
        if (card.parentContainer != undefined) {
            card.parentContainer.remove(card);
        }
        this.cardsContainer.addAt(card, cardIndex);
        Phaser.Utils.Array.AddAt(this.cards, card, cardIndex);

        // update cards to be frontfacing (or backfacing)
        this.UpdateFacingAll(this.playerProps.cardsContainer.frontFacing);

        // reorder cards data model
        this.ReorderCards(
            animate, // animate?: boolean
            trumpSuit, // trumpSuit?: TrumpSuit
            rankLeftToRight, // rankLeftToRight?: boolean
            stackLeftToRight, // viewLeftToRight?: boolean
            false, // skipReorder?
        );
    }

    public HasCards(): boolean {
        return this.cards.length > 0;
    }

    public GetCards(): Card[] {
        return this.cards;
    }

    /**
     * Returns cards in hand as CardData[]
     */
    public GetCardDatas(): CardData[] {
        // dont mind creating new array for this always
        return this.cards.map((card: Card) => {
            return { rank: card.rank, suit: card.suit };
        });
    }

    public GetCardByIndex(cardIndex: number): Card {
        // console.log(this.cards, cardIndex)
        return this.cards[cardIndex];
    }

    public PickCardsByIndex(cardIndexes: number[]): Card[] {
        cardIndexes.sort(function (a, b) { return b - a }); // descending sort; mutates array
        const returnedCards: Card[] = [];
        cardIndexes.forEach(cardIndex => {
            const card: Card = this.PickCardByIndex(cardIndex);
            returnedCards.push(card);
        });
        return returnedCards;
    }

    public PickCardByIndex(cardIndex: number): Card {
        // console.log(this.cards, cardIndex)

        // modifies in place
        const card: Card = Phaser.Utils.Array.RemoveAt(this.cards, cardIndex);

        // TODO: possibly not necessary soon @see https://github.com/photonstorm/phaser/issues/6091
        this.cardsContainer.remove(card);
        return card;
    }

    public PickCards(cards: Card[]): Card[] {
        cards.forEach(card => {
            // modifies in place
            Phaser.Utils.Array.Remove(this.cards, card);
            // TODO: possibly not necessary soon @see https://github.com/photonstorm/phaser/issues/6091
            this.cardsContainer.remove(card);
        });

        return cards;
    }

    public PickCard(card: Card): Card {
        // modifies in place
        Phaser.Utils.Array.Remove(this.cards, card);

        // TODO: possibly not necessary soon @see https://github.com/photonstorm/phaser/issues/6091
        this.cardsContainer.remove(card);

        return card;
    }

    public PickAll(): Card[] {
        const returnCards: Card[] = this.cards.slice();
        this.cards = [];
        this.cardsContainer.removeAll(); // destroyChild? default:false
        return returnCards;
    }

    public OnResize(force: boolean = false): void {
        // console.log("OnResize");

        // only resize if not hidden
        if (this.isShowing || force) {
            let avatarContainerScale: number;
            let avatarScale: number;
            let acceptorBubbleScale: number;
            let cardsContainerScale: number;
            let cardsContainerBias: number;
            let cardsScreenEdgeOffset: number;
            // const cardsFrontFacing = this.playerProps.cards.frontFacing;
            let avatarContainerBias: number;
            // let avatarScreenEdgeOffset: number;
            let avatarScreenEdgeOffset: number;
            let bubbleLayout;
            const screenOrientationType: ScreenOrientationType = ScreenManager.GetOrientation();
            switch (screenOrientationType) {
                case ScreenOrientationType.LANDSCAPE:
                    // console.log("landscape")
                    cardsContainerScale = this.playerProps.cardsContainer.landscape.scale;
                    avatarScale = this.playerProps.avatar.landscape.scale;
                    acceptorBubbleScale = this.playerProps.acceptorBubble?.landscape.scale;
                    cardsContainerBias = this.playerProps.cardsContainer.landscape.bias;
                    avatarContainerScale = this.playerProps.avatarContainer.landscape.scale;
                    avatarContainerBias = this.playerProps.avatarContainer.landscape.bias;
                    cardsScreenEdgeOffset = this.playerProps.cardsContainer.landscape.screenEdgeOffset;
                    avatarScreenEdgeOffset = this.playerProps.avatarContainer.landscape.screenEdgeOffset;
                    bubbleLayout = this.playerProps.speechBubble.landscape;
                    break;
                case ScreenOrientationType.PORTRAIT:
                    // console.log("portrait")
                    cardsContainerScale = this.playerProps.cardsContainer.portrait.scale;
                    avatarScale = this.playerProps.avatar.portrait.scale;
                    acceptorBubbleScale = this.playerProps.acceptorBubble?.portrait.scale;
                    cardsContainerBias = this.playerProps.cardsContainer.portrait.bias;
                    avatarContainerScale = this.playerProps.avatarContainer.portrait.scale;
                    avatarContainerBias = this.playerProps.avatarContainer.portrait.bias;
                    cardsScreenEdgeOffset = this.playerProps.cardsContainer.portrait.screenEdgeOffset;
                    avatarScreenEdgeOffset = this.playerProps.avatarContainer.portrait.screenEdgeOffset;
                    bubbleLayout = this.playerProps.speechBubble.portrait;
                    break;
            }

            cardsContainerScale = ScreenManager.GetAssetScale(cardsContainerScale, cardsContainerBias);
            avatarContainerScale = ScreenManager.GetAssetScale(avatarContainerScale, avatarContainerBias);

            this.cardsContainer.scale = cardsContainerScale;
            this.cardsContainer.setPosition(
                cardsScreenEdgeOffset * this.playerProps.facing.x * cardsContainerScale,
                cardsScreenEdgeOffset * this.playerProps.facing.y * cardsContainerScale
            );
            
            // reposition contentHolder BUT scale avatarContainer (contentHolder scales from Wakeup animation)
            this.avatarContainer.scale = avatarContainerScale;
            this.contentHolder.setPosition(
                avatarScreenEdgeOffset * this.playerProps.facing.x * avatarContainerScale,
                avatarScreenEdgeOffset * this.playerProps.facing.y * avatarContainerScale,
            );

            this.avatar.scale = avatarScale;
            this.avatarBg.setPosition(
                this.avatar.x + this.avatar.displayWidth * 0.5 * this.playerProps.facing.x,
                this.avatar.y + this.avatar.displayHeight * 0.5 * this.playerProps.facing.y,
            );
            this.avatarBg.scale = avatarScale;

            this.chipContainer.scale = avatarScale;
            this.chipContainer.setPosition(
                this.avatarBg.x,
                this.avatarBg.y,
            );

            this.speechBubble.setPosition(
                (0.5 - this.playerProps.dX) * this.avatar.displayWidth 
                    + 0.5 * bubbleLayout.facing.x * this.avatar.displayWidth,
                (0.5 - this.playerProps.dY) * this.avatar.displayHeight 
                    + 0.5 * bubbleLayout.facing.y * this.avatar.displayHeight,
            );

            // origin and grow bubble content considering current orientation settings
            this.speechBubble.SetFacingAndGrowthDirection(bubbleLayout.facing, bubbleLayout.contentGrowthX, true); // udpateView

            if (this.isPlayer) {
                // EDIT: since the player speechBubble can have varying orientation based on screen orient,
                //       acceptorBubble will be made to ALWAYS FACE STRAIGHT UPWARDS.
                // this.acceptorBubble.copyPosition(this.speechBubble);
                this.acceptorBubble.setPosition(0, -this.avatar.displayHeight);
                this.acceptorBubble.scale = acceptorBubbleScale;
            }
        }
    }

    /**
     * On this level, Orientation switch causes card spacing and angle change and ReorderCards (no animation) trigger (once per orientation change)
     * 
     * @param screenOrientationType 
     */
    public OnOrientationChanged(screenOrientationType: ScreenOrientationType): void {
        // console.log("OnOrientationChanged");

        // if any cards to sort
        if (this.cards.length) {
            // kill any ongoing reorder animation
            this.KillReorderCards();
            // sort without animation based on orientation
            this.ReorderCards(
                false, // animate?: boolean
                this.table.GetStateMachine().GetData().trumpSuit, // trumpSuit?: TrumpSuit
                OptionsDataManager.IsRankLeftToRight(), // rankLeftToRight?: boolean
                OptionsDataManager.IsStackLeftToRight(), // viewLeftToRight?: boolean
                false, // skipReorder?
                this.curPoppedCards, // cached popped cards to re-pop in the new orient
            );
        }
    }

    public UpdateFacingAll(frontFacing: boolean): void {
        this.cards.forEach(card => card.SetFrontFacing(frontFacing));
    }

    /**
     * Order hand cards
     * first by suit
     * then by value (which is based on card's suit being either trump or other)
     * Default value-order direction is from right to left, but can be overriden @see rankLeftToRight
     * Default view-stacking direction is from left to right, but can be overriden @see viewLeftToRight
     * (by default by rank from right to left)
     * (however visually they are added from left ro right)
     * @param animate should cards be animated after reorder?
     * @param trumpSuits which suits are trumps? this affects sorting calculations by rank.
     * @param rankLeftToRight card ranks are sorted withing their suits ascending R2L (or L2R)
     *                        Also: @see CardsManager.suitOrder is in this direction too
     * @param stackLeftToRight order in which cards cover other cards
     * @param onComplete Triggers when reordercards completes
     */
    public ReorderCards(
        animateReorder: boolean = true,
        trumpSuit: TrumpSuit = TrumpSuit.UNDEFINED,
        rankLeftToRight: boolean = true,
        stackLeftToRight: boolean = true,
        skipReorder: boolean = false,
        popCards: Card[] = undefined,
    ): void {
        // console.log("ReorderCards");
        // console.log(this.cards.map(card=>card.log()));
        
        // make sure to kill ongoing ReorderCards animation (if applicable)
        // EDIT: potentionally very dangerous
        // console.log(this.index);
        

        // skip reordering for AI/bot players
        // const skipReorder:boolean = this.index != 2;
        // const skipReorder:boolean = false;
        if (this.isPlayer && !skipReorder) {
            // DEBUG
            // rankLeftToRight = true;
            // rankLeftToRight = false;

            // const orderedCards: Card[] = [];
            const cardsBySuit: Card[][] = [];
            // console.log(this.cards.length)
            // create suit collections
            while (this.cards.length > 0) {
                const card: Card = this.cards.pop();
                if (cardsBySuit[card.suit] == undefined) {
                    cardsBySuit[card.suit] = [];
                }
                cardsBySuit[card.suit].push(card);
            }
            // console.log(cardsBySuit.map(suitCards=>suitCards.map(card=>card.log())));

            // suit order: from CardsManager OR SMART if avoidSameCardColorNeighbour=true

            let suitOrder: CardSuit[] = [];
            const avoidSameCardColorNeighbour: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.avoidSameCardColorNeighbour, this.table.variationId);
            if (avoidSameCardColorNeighbour && (avoidSameCardColorNeighbour.value as boolean)) {
                // check for current cards, sort based on what we got 
                // (suit can be missing, place it in the middle.. etc)
                let redSuits: CardSuit[] = [];
                let blackSuits: CardSuit[] = [];
                cardsBySuit.forEach((suitCards: Card[], i: number) => {
                    // skip undefined (suit doesnt exist on hand)
                    if (suitCards) {
                        if (suitCards[0].color == CardColor.BLACK) {
                            blackSuits.push(suitCards[0].suit);
                        }
                        else {
                            redSuits.push(suitCards[0].suit);
                        }
                    }
                });
                let numSuits: number = redSuits.length + blackSuits.length;
                // console.log("numSuits=", numSuits);

                switch (numSuits) {
                    case 4:
                        suitOrder = CardsManager.avoidSameCardColorNeighbourSuitOrder;
                        break;

                    // avoid B,B,R || R,B,B || R,R,B || B,R,R suits order
                    case 3:
                        // console.log(numSuits, redSuits.length, blackSuits.length);

                        // CardsManager.avoidSameCardColorNeighbourSuitOrder:
                        // CardSuit.HEARTS   (index:0),   // symbol: ♥ - RED
                        // CardSuit.CLUBS    (index:1),   // symbol: ♣ - BLACK
                        // CardSuit.DIAMONDS (index:2),   // symbol: ♦ - RED
                        // CardSuit.SPADES   (index:3)    // symbol: ♠ - BLACK

                        // Case A) 2 black + 1 red (order: Clubs, RED, Spades):
                        if (blackSuits.length == 2) {
                            // console.log("3: 2red 1black");
                            let redSuit: CardSuit;
                            for (let i: number = 0; i < cardsBySuit.length; i++) {
                                if (cardsBySuit[i]) {
                                    if (cardsBySuit[i][0].color == CardColor.RED) {
                                        redSuit = cardsBySuit[i][0].suit;
                                        break;
                                    }
                                }
                            }
                            // console.log(">",suitOrder);
                            suitOrder[0] = CardSuit.CLUBS;
                            suitOrder[1] = redSuit;
                            suitOrder[2] = CardSuit.SPADES;
                            suitOrder[3] = GetOppositeColorSuitToSuit(redSuit);
                        }
                        // Case B) 2red + 1 black (order: Hearts, BLACK, Diamonds):
                        else {
                            // console.log("3: 2black 1red");
                            let blackSuit: CardSuit;
                            for (let i: number = 0; i < cardsBySuit.length; i++) {
                                if (cardsBySuit[i]) {
                                    if (cardsBySuit[i][0].color == CardColor.BLACK) {
                                        blackSuit = cardsBySuit[i][0].suit;
                                        break;
                                    }
                                }
                            }
                            suitOrder[0] = CardSuit.HEARTS;
                            suitOrder[1] = blackSuit;
                            suitOrder[2] = CardSuit.DIAMONDS;
                            suitOrder[3] = GetOppositeColorSuitToSuit(blackSuit);
                        }
                        // console.log("suitOrder=",suitOrder);

                        // suitOrder = CardsManager.suitOrder;
                        break;

                    case 2:
                    case 1:
                        suitOrder = CardsManager.avoidSameCardColorNeighbourSuitOrder;
                        break;
                    case 0:
                        // console.log("WARN: No cards to sort."); // can happen at the end of last trick (its fine)
                        // console.trace(this.cards.length)
                        suitOrder = CardsManager.avoidSameCardColorNeighbourSuitOrder;
                        break;
                }
            }
            else {
                // console.log("setting default BBRR order");
                suitOrder = CardsManager.noAvoidSameCardColorNeighbourSuitOrder;
            }
            // console.log("["+this.index+"] suitOrder:",suitOrder.map(suit=>CardSuit[suit]));

            // console.log(1, cardsBySuit.map(suitCards=>suitCards.map(card=>`${CardSuit[card.suit]} ${CardRank[card.rank]}`).join()));
            // sort cards in suit
            for (let i = 0; i < suitOrder.length; i++) {
                const cardSuit: CardSuit = suitOrder[i];
                // console.log("SUITORDER: "+i+":",CardSuit[cardSuit]);
                const cardSuitAsTrumpSuit: TrumpSuit = CardSuitToTrumpSuit(cardSuit);
                const suitCards: Card[] = cardsBySuit[cardSuit];
                if (suitCards) {
                    // console.log(1, cardsBySuit[cardSuit].map(card=>card.log()))
                    if (trumpSuit == TrumpSuit.ALLTRUMPS) {
                        // sorts in place
                        suitCards.sort(this.SortAllTrumpsSuitCards);
                    }
                    else
                        if (trumpSuit == TrumpSuit.NOTRUMPS) {
                            suitCards.sort(this.SortNoTrumpsSuitCards);
                        }
                        else {
                            // if card(s) suit is trump suit
                            if (trumpSuit == cardSuitAsTrumpSuit) {
                                suitCards.sort(this.SortTrumpSuitCards);
                            }
                            // if card(s) suit is not a trump suit
                            // NOTE: this also includes @see TrumpSuit.UNDEFINED eg 'before trump suit selected' card sorting (sorts them as 'other suit)
                            else {
                                suitCards.sort(this.SortOtherSuitCards);
                            }
                        }
                    // console.log("sorted "+suitCards.map(card=>card.log())+" with trumpSuit sort: "+TrumpSuit[trumpSuit]);

                    if (!rankLeftToRight) {
                        // modifies in place
                        suitCards.reverse();
                    }

                    // console.log(2, cardsBySuit[cardSuit].map(card=>card.log()))

                    // put cardsBySuit back into single contiguous array
                    // add sorted suitCards back to cards array (also in CardsManager.suitOrder)
                    // console.log(suitCards.map(card=>card.log()));
                    this.cards = this.cards.concat(suitCards);
                    // console.log("concat cards:",this.cards.map(card=>card.log()));
                }
            }
        }
        // if (this.isPlayer) {
        // //     console.log("*",this.cards.map(card=>`${card.log()} ${card.view.frame.name}`));
        //     console.log("*",this.cards.map(card=>`${card.log()}`));
        // }

        // animate cards to place
        const screenOrientationType: ScreenOrientationType = ScreenManager.GetOrientation();
        let cardsSpacing: number;
        let cardsCirclePosition: Phaser.Geom.Point;
        let poppedCardsCirclePosition: Phaser.Geom.Point;
        let cardsCircleRadius: number;
        let cardAngleDeviance: number;
        switch (screenOrientationType) {
            case ScreenOrientationType.LANDSCAPE:
                cardsSpacing = this.cardsProps.landscape.cardsSpacing;
                cardsCirclePosition = this.cardsProps.landscape.cardsCirclePosition;
                poppedCardsCirclePosition = this.cardsProps.landscape.poppedCardsCirclePosition;
                cardsCircleRadius = this.cardsProps.landscape.cardsCircleRadius;
                cardAngleDeviance = this.cardsProps.landscape.cardAngleDeviance;
                break;
            case ScreenOrientationType.PORTRAIT:
                cardsSpacing = this.cardsProps.portrait.cardsSpacing;
                cardsCirclePosition = this.cardsProps.portrait.cardsCirclePosition;
                poppedCardsCirclePosition = this.cardsProps.portrait.poppedCardsCirclePosition;
                cardsCircleRadius = this.cardsProps.portrait.cardsCircleRadius;
                cardAngleDeviance = this.cardsProps.portrait.cardAngleDeviance;
                break;
        }

        for (let i = 0; i < this.cards.length; i++) {
            const card: Card = this.cards[i];

            // fix semi transparent cards due to flipping screen 
            // during MajorDeclaration alpha fadeOut (500ms)
            this.scene.tweens.killTweensOf(card);
            card.alpha = 1;

            const angle: number = (i - ((this.cards.length - 1) / 2)) * cardsSpacing; // angle
            let pt: Phaser.Geom.Point;
            if ((popCards != undefined) && (popCards.indexOf(card) != -1)) {
                pt = GetPointOnCircle(
                    poppedCardsCirclePosition, // circ center pos
                    cardsCircleRadius, // circ radius
                    -Math.PI / 2 + Phaser.Math.DegToRad(angle) // pos on circle
                );
            }
            else {
                pt = GetPointOnCircle(
                    cardsCirclePosition, // circ center pos
                    cardsCircleRadius, // circ radius
                    -Math.PI / 2 + Phaser.Math.DegToRad(angle) // pos on circle
                );
            }
            // stacking left to right
            if (stackLeftToRight) {
                this.cardsContainer.bringToTop(card);
            }
            // stacking right to left
            else {
                this.cardsContainer.sendToBack(card);
            }
            const cardReorderDuration = OptionsDataManager.GetTiming(AppTimings.cardReorderDuration);
            if (animateReorder) {
                // if (false) {
                this.scene.tweens.add({
                    targets: card,
                    x: pt.x,
                    y: pt.y,
                    angle: angle * cardAngleDeviance,
                    scale: 1,
                    duration: cardReorderDuration,
                    ease: AppSettings.ease.genericShowHideEase,
                    delay: i * 50,
                })
            }
            else {
                card.setPosition(pt.x, pt.y);
                card.setAngle(angle * cardAngleDeviance);
                // if (this.isPlayer) console.log(angle * this.cardAngleDeviance, card.log());
                card.setScale(1);
            }
        }

        // for orient switch (pops last popped cards, unless undefined)
        this.curPoppedCards = popCards == undefined ? [] : popCards;
        // console.log(this.curPoppedCards?.map(card=>card.log()));

        // console.log(this.cards.map(card=>card.log()));
    }

    /**
     * Kills any ongoing animation on player's cards
     */
    public KillReorderCards(): void {
        this.cards.forEach(card=>{
            this.scene.tweens.killTweensOf(card);
        });
    }

    private GetFacingAngle(): number {
        if (this.playerProps.facing.x == 0) {
            if (this.playerProps.facing.y == 1) {
                return 180;
            }
            else {
                return 0;
            }
        }
        else if (this.playerProps.facing.y == 0) {
            if (this.playerProps.facing.x == 1) {
                return 90;
            }
            else {
                return 270;
            }
        }
        return 0;
    }

    private SortTrumpSuitCards(cardA: Card, cardB: Card): number {
        if (CardsManager.cardRankToCardProps[cardA.rank].rankOrderTrump > CardsManager.cardRankToCardProps[cardB.rank].rankOrderTrump) return 1;
        else if (CardsManager.cardRankToCardProps[cardA.rank].rankOrderTrump < CardsManager.cardRankToCardProps[cardB.rank].rankOrderTrump) return -1;
        else return 0;
    }

    private SortOtherSuitCards(cardA: Card, cardB: Card): number {
        if (CardsManager.cardRankToCardProps[cardA.rank].rankOrderOther > CardsManager.cardRankToCardProps[cardB.rank].rankOrderOther) return 1;
        else if (CardsManager.cardRankToCardProps[cardA.rank].rankOrderOther < CardsManager.cardRankToCardProps[cardB.rank].rankOrderOther) return -1;
        else return 0;
    }

    /**
     * NOTE: NT and NT130 has the same rank order (only some ranks have diff. values)
     * @param cardA 
     * @param cardB 
     * @returns 
     */
    private SortNoTrumpsSuitCards(cardA: Card, cardB: Card): number {
        if (CardsManager.cardRankToCardProps[cardA.rank].rankOrderNoTrumps > CardsManager.cardRankToCardProps[cardB.rank].rankOrderNoTrumps) return 1;
        else if (CardsManager.cardRankToCardProps[cardA.rank].rankOrderNoTrumps < CardsManager.cardRankToCardProps[cardB.rank].rankOrderNoTrumps) return -1;
        else return 0;
    }

    /**
     * NOTE: AT and AT258 has the same rank order (only some ranks have diff. values)
     * @param cardA 
     * @param cardB 
     * @returns 
     */
    private SortAllTrumpsSuitCards(cardA: Card, cardB: Card): number {
        if (CardsManager.cardRankToCardProps[cardA.rank].rankOrderAllTrumps > CardsManager.cardRankToCardProps[cardB.rank].rankOrderAllTrumps) return 1;
        else if (CardsManager.cardRankToCardProps[cardA.rank].rankOrderAllTrumps < CardsManager.cardRankToCardProps[cardB.rank].rankOrderAllTrumps) return -1;
        else return 0;
    }
}
